import { Box } from '@mui/material'
import NextImage from 'next/image'
import VariantImages from '@/assets/VariantImages'
import { EmtButton, EmtModal, EmtTypography } from 'emt-components-v2'
import { useState } from 'react'
import { useIsQueryOwner, useIsResponsive } from '@/hooks'
import FileDropZone from './EMT/FileDropZone'
import { useAvatarStore } from '@/store/zustand'
import { useNotifications } from '@/modules/Notifications'
import { UserServiceHelper } from '@/modules/User'
import { useEMTDispatch } from '@/store'
import { TFunction } from 'i18next'
import useCurrentUser from '@/hooks/useCurrentUser'
import { arrayBufferToBase64 } from '@/utils'

export const ModalAddAvatarImage = ({ t }: { t: TFunction<'common', undefined> }) => {
  const [successStep, setSuccessStep] = useState<boolean>(false)
  const { setActiveAvatarModal, activeAvatarModal, setIsThereAvatar } = useAvatarStore()
  const [isLoading, setIsLoading] = useState(false)

  const handleCloseModal = () => {
    if (isLoading) return
    setActiveAvatarModal(false)
    successStep ? setIsThereAvatar(true) : null
  }

  return (
    <EmtModal
      open={activeAvatarModal}
      onClose={handleCloseModal}
      disableScrollLock
      sx={{
        '& .emt-icons': {
          fontSize: 14,
          color: (theme: any) => theme.palette.greyGradient[550],
          top: 20,
        },
      }}
    >
      {successStep ? (
        <ValidationStep t={t} />
      ) : (
        <AskAvatarStep t={t} isLoading={isLoading} setIsLoading={setIsLoading} setSuccessStep={setSuccessStep} />
      )}
    </EmtModal>
  )
}

const AskAvatarStep = ({
  t,
  setSuccessStep,
  setIsLoading,
  isLoading,
}: {
  t: TFunction<'common', undefined>
  setSuccessStep: (step: boolean) => void
  setIsLoading: (res: boolean) => void
  isLoading: boolean
}) => {
  const [isQueryOwner] = useIsQueryOwner()
  const dispatch = useEMTDispatch()

  // Base64
  const [preview, setPreview] = useState<string | ArrayBuffer | undefined>()
  const [addNotificationError] = useNotifications()
  const { avatar } = useCurrentUser()

  const onSubmit = async () => {
    if (!preview) return
    try {
      setIsLoading(true)
      if (avatar) {
        await UserServiceHelper.deletePictureAsync(avatar.id)
      }
      await UserServiceHelper.addMemberPictureAsync({
        base64: arrayBufferToBase64(preview),
        isMain: true,
      })
      await UserServiceHelper.dispatchGetMyInfoAsync(dispatch)()
      setSuccessStep(true)
      setIsLoading(false)
    } catch (error) {
      const fields = addNotificationError({
        error,
      })
      console.warn('Update Avatar Error', error.message, fields)
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          textAlign: 'center',
        }}
      >
        <NextImage src={VariantImages.AnalyticIcon} width={74} quality={100} alt={''} />
        <EmtTypography variant="h4" fontWeight={800}>
          {t('bugPhoto.title')}
        </EmtTypography>
        <EmtTypography variant="h5">{t('bugPhoto.text')}</EmtTypography>
        {/* 👤 Avatar 👤 */}
        <FileDropZone
          variant="default"
          isOwner={isQueryOwner}
          maxFiles={1}
          multipleFiles={false}
          dark
          onDrop={(file) => setPreview(file as string | ArrayBuffer)}
          overrideDropZoneBox={{
            borderColor: (theme: any) => (preview ? 'transparent' : theme.palette.greyGradient[200]),
            backgroundColor: (theme: any) => theme.palette.greyGradient[100],
            transition: 'none',
            width: '212px',
            height: '212px',
            '& .emt-icons': {
              bottom: '-16px',
              top: 'inherit',
              color: (theme: any) => theme.palette.primary.main,
            },
          }}
          preview={preview}
          activeDeleteButton={false}
          activeRotation={false}
        />

        {/* 👤 END Avatar 👤 */}
        <Box sx={{ width: '100%', display: 'flex', gap: '15px', marginTop: '25px', justifyContent: 'center' }}>
          <EmtButton
            loader={isLoading}
            onClick={onSubmit}
            disabled={!preview}
            size={'large'}
            sx={{ paddingY: '10px', paddingX: '40px' }}
          >
            {t('bugPhoto.modal.button')}
          </EmtButton>
        </Box>
      </Box>
    </>
  )
}

const ValidationStep = ({ t }: { t: TFunction<'common', undefined> }) => {
  const [isMobile] = useIsResponsive()
  const { setActiveAvatarModal, setIsThereAvatar } = useAvatarStore()

  const endStep = () => {
    setActiveAvatarModal(false)
    setIsThereAvatar(true)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          textAlign: 'center',
        }}
      >
        <NextImage src={VariantImages.CheckValidIcon} width={74} quality={100} alt={''} />
        <EmtTypography variant="h4" fontWeight={800}>
          {t('bugPhoto.success.title')}
        </EmtTypography>
        <Box sx={{ width: '100%', display: 'flex', gap: '15px', flexDirection: 'column' }}>
          <EmtButton
            variant="contained"
            fullWidth={isMobile}
            size={'large'}
            onClick={endStep}
            sx={{ paddingY: '10px', paddingX: !isMobile ? '70px' : '' }}
          >
            {t('bugPhoto.success.button')}
          </EmtButton>
        </Box>
      </Box>
    </>
  )
}
